import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { Loader2 } from "lucide-react";
import { db } from "@/lib/firebase";
import { useAppStore } from "@/lib/store";
import { Order } from "@/lib/types";
import { formatPrice } from "@/lib/utils";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import ImmersiveBackground from "@/components/ui/ImmersiveBackground";
import RevealOnScroll from "@/components/ui/RevealOnScroll";

export default function CheckoutSuccessPage() {
  useDocumentMeta({
    title: "Order Confirmed",
    description: "Your order on Nerd Multiverse.",
    path: "/checkout/success",
    noindex: true,
  });

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const clearCart = useAppStore((s) => s.clearCart);
  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    if (!orderId || !db) return;
    const unsub = onSnapshot(doc(db, "orders", orderId), (snap) => {
      if (snap.exists()) setOrder(snap.data() as Order);
    });
    return unsub;
  }, [orderId]);

  useEffect(() => {
    if (order?.paymentStatus === "paid") clearCart();
  }, [order?.paymentStatus, clearCart]);

  const isPaid = order?.paymentStatus === "paid";
  const isFailed = order?.paymentStatus === "failed";

  return (
    <div className="relative pt-40 pb-24 min-h-dvh flex items-center justify-center text-center px-4">
      <ImmersiveBackground src="/images/hero/cart-v2.webp" focus="50% 25%" />
      <RevealOnScroll>
        {!orderId ? (
          <>
            <p className="font-display text-3xl text-white">Nothing to confirm.</p>
            <p className="mt-3 text-[var(--nm-ink-muted)]">
              We couldn&apos;t find an order to check.
            </p>
          </>
        ) : isFailed ? (
          <>
            <p className="font-display text-3xl text-white">Payment didn&apos;t go through.</p>
            <p className="mt-3 text-[var(--nm-ink-muted)]">
              You haven&apos;t been charged. Your cart is still waiting for you.
            </p>
          </>
        ) : isPaid ? (
          <>
            <p className="font-display text-3xl text-white">Order confirmed.</p>
            <p className="mt-3 text-[var(--nm-ink-muted)]">
              {order && `${formatPrice(order.total)} · `}Your finds are on their way from
              sellers across the Multiverse.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto animate-spin text-white" size={28} />
            <p className="mt-4 font-display text-2xl text-white">Confirming your payment…</p>
            <p className="mt-2 text-[var(--nm-ink-muted)]">
              This usually only takes a second or two.
            </p>
          </>
        )}

        <Link
          to="/marketplace"
          className="mt-8 inline-block px-7 py-3.5 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors"
        >
          KEEP EXPLORING
        </Link>
      </RevealOnScroll>
    </div>
  );
}
